import type { components } from '@/api'
import type { Field } from '@/modules/Project/Fields/types'
import type { Property } from '@/modules/Project/Properties/types'
import { computed, type ComputedRef, type Ref } from 'vue'

export type Staleness = 'stale' | 'maybe-stale' | null

type BackendStalenessReason =
  components['schemas']['Projects.ListEntities.FieldStaleness.Stale']['changes'][number]

export type DotIndicatorReason =
  | {
      cause: 'stale'
      detail?: BackendStalenessReason
    }
  | { cause: 'error'; detail: string | null }
  /**
   * When hasn't completely errored, but there was some extra error returned.
   * See GO-3170. This can happen but I'm not sure how to trigger it.
   */
  | { cause: 'complete-with-error'; detail: string }
  | { cause: 'computing' }
  | { cause: 'locked' }

/**
 * In the UI, we often show a dot to the side of a field to give some indication of its
 * status. This composable returns the reason for the dot's appearance, or null if we
 * don't need to show the dot.
 *
 * See https://linear.app/v7labs/issue/GO-3498/expose-actual-stale-status-of-a-field
 * for notes on what the different staleness statuses and reasons mean.
 */
export const useDotIndicator = (
  field: Ref<Field | undefined>,
  property: Ref<Property | undefined>,
): ComputedRef<DotIndicatorReason | null> => {
  return computed<DotIndicatorReason | null>(() => {
    if (!field.value) {
      return null
    }

    if (field.value.status === 'computing') {
      return { cause: 'computing' }
    }

    if (field.value.status === 'error') {
      return { cause: 'error', detail: field.value.errorMessage }
    }

    if (field.value.errorMessage) {
      return { cause: 'complete-with-error', detail: field.value.errorMessage }
    }

    if (field.value.staleness.status === 'stale') {
      return { cause: 'stale', detail: field.value.staleness.changes[0] }
    }

    if (field.value.staleness.status === 'unknown') {
      if (field.value.staleness.reason === 'immutable_field') {
        return { cause: 'locked' }
      }
    }

    if (
      typeof field.value.propertyHash === 'string' &&
      field.value.propertyHash !== property.value?.hash
    ) {
      return { cause: 'stale' }
    }

    return null
  })
}
